import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpHandlerFn,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, throwError } from 'rxjs';
import { catchError, filter, switchMap, take } from 'rxjs/operators';
import { AuthService } from '../services/auth/auth.service';
import { LoginErrors, LoginResponse } from '../models/auth.model';

let isRefreshing = false;
const refreshTokenSubject: BehaviorSubject<string | null> = new BehaviorSubject<string | null>(
  null,
);

export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const router = inject(Router);
  const tokens = authService.getToken();

  let authReq = req;
  if (tokens?.accessToken) {
    authReq = addTokenHeader(req, tokens.accessToken);
  }

  return next(authReq).pipe(
    catchError((error) => {
      if (
        error instanceof HttpErrorResponse &&
        error.status === 401 &&
        !req.url.includes('auth/refresh')
      ) {
        return handle401Error(authReq, next, authService, router);
      }

      return throwError(() => error);
    }),
  );
};

const addTokenHeader = (request: HttpRequest<unknown>, token: string) => {
  return request.clone({
    setHeaders: { Authorization: `Bearer ${token}` },
  });
};

const handle401Error = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  router: Router,
) => {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshTokenSubject.next(null);

    const tokens = authService.getToken();

    if (tokens?.refreshToken) {
      return authService.refreshToken(tokens.refreshToken).pipe(
        switchMap((response: LoginResponse) => {
          isRefreshing = false;
          authService.saveToken(response);
          refreshTokenSubject.next(response.accessToken);

          return next(addTokenHeader(request, response.accessToken));
        }),
        catchError((err) => {
          isRefreshing = false;
          authService.logout();
          router.navigate(['/auth/login']);
          return throwError(() => err);
        }),
      );
    } else {
      isRefreshing = false;
      authService.logout();
      router.navigate(['/auth/login']);
      return throwError(
        () =>
          new HttpErrorResponse({
            status: 401,
            error: {
              error: LoginErrors.InvalidUserCredentialsExceptionError,
            },
          }),
      );
    }
  } else {
    return refreshTokenSubject.pipe(
      filter((token) => token !== null),
      take(1),
      switchMap((token) => {
        return next(addTokenHeader(request, token as string));
      }),
    );
  }
};
